// Manual del Sistema InsightSoft ERP
// Archivo de contenido — edita aquí para actualizar el manual sin tocar la UI

export const MANUAL_VERSION = '2.0';
export const MANUAL_FECHA   = 'Junio 2026';

export const CATEGORIAS = [
  { id: 'todas',        label: 'Todo el sistema' },
  { id: 'comercial',    label: 'Comercial' },
  { id: 'electronico',  label: 'Electrónico SUNAT' },
  { id: 'inventario',   label: 'Inventario' },
  { id: 'finanzas',     label: 'Finanzas' },
  { id: 'contabilidad', label: 'Contabilidad' },
  { id: 'rrhh',         label: 'RR.HH.' },
  { id: 'admin',        label: 'Administración' },
];

// ─────────────────────────────────────────────────────────────────
// ESTRUCTURA DE CADA MÓDULO:
//   id, requiereModulo, titulo, descripcion, icono (string MUI),
//   colorHex, categoria, secciones[]
//
// ESTRUCTURA DE SECCIÓN:
//   titulo, icono (string), pasos[]
//
// ESTRUCTURA DE PASO:
//   num, titulo, detalle (string | string[]),
//   tip? (consejo verde), alerta? (advertencia naranja),
//   peligro? (rojo), nota? (azul)
// ─────────────────────────────────────────────────────────────────

const MODULOS = [

  // ══════════════════════════════════════════════════════════════
  // PRIMEROS PASOS
  // ══════════════════════════════════════════════════════════════
  {
    id: 'primeros_pasos',
    titulo: 'Primeros Pasos',
    descripcion: 'Cómo ingresar al sistema y entender la pantalla principal, pensado para quien nunca lo usó.',
    icono: 'Help',
    colorHex: '#37474f',
    categoria: 'admin',
    secciones: [
      {
        titulo: 'Iniciar sesión',
        icono: 'Key',
        pasos: [
          {
            num: 1, titulo: 'Abrir la dirección web del sistema',
            detalle: 'Escribe en el navegador la dirección que te dio tu administrador (por ejemplo tuempresa.insightsoftsolutions.tech). Vas a ver la pantalla de inicio de sesión.',
            imagen: '/manual/login/01-login-vacio.png',
          },
          {
            num: 2, titulo: 'Escribir tu usuario y contraseña',
            detalle: 'Ambos te los entrega el administrador del sistema cuando te da de alta. Respeta mayúsculas y minúsculas en la contraseña.',
            imagen: '/manual/login/02-login-lleno.png',
            tip: 'Si te equivocas varias veces, no pasa nada grave: solo vuelve a escribir con calma. Si de verdad olvidaste la contraseña, pídele al administrador que te la cambie desde Gestión de Usuarios.',
          },
          {
            num: 3, titulo: 'La pantalla principal (Dashboard)',
            detalle: 'Al entrar ves un panel con las ventas del mes, compras, balance de tesorería y gráficos generales. No necesitas entender todos los números al principio — es solo un resumen. Lo importante para empezar a trabajar está en el menú de la izquierda.',
            imagen: '/manual/login/03-dashboard.png',
          },
          {
            num: 4, titulo: 'El menú lateral',
            detalle: 'Haz clic en el ícono ☰ (tres rayitas) arriba a la izquierda para abrir o cerrar el menú. Ahí están todos los módulos organizados por grupo: Mantenimiento (productos, categorías, clientes...), Facturación (cotizaciones, ventas, comprobantes...), Logística (compras, kardex...), Tesorería, Contabilidad, etc.',
            nota: 'No vas a ver todos los grupos si tu usuario no tiene acceso a ese módulo — es normal, depende de los permisos que te asignó el administrador.',
          },
        ],
      },
    ],
  },

  // ══════════════════════════════════════════════════════════════
  // PRODUCTOS E INVENTARIO
  // ══════════════════════════════════════════════════════════════
  {
    id: 'productos',
    requiereModulo: 'logistica',
    titulo: 'Productos e Inventario',
    descripcion: 'Crear y gestionar productos, precios, stock y unidades de medida.',
    icono: 'Inventory2',
    colorHex: '#2e7d32',
    categoria: 'inventario',
    secciones: [
      {
        titulo: 'Crear un producto nuevo',
        icono: 'AddBox',
        pasos: [
          {
            num: 1, titulo: 'Ir al módulo Productos',
            detalle: 'En el menú lateral (el ícono ☰ arriba a la izquierda) despliega "Mantenimiento" y haz clic en "Productos". Se abre la lista de todos los productos ya registrados, con columnas de código, nombre, categoría, precio y stock.',
            imagen: '/manual/productos/01-lista.png',
            tip: 'Si tienes muchos productos, usa el buscador de arriba o el filtro por Categoría/Marca para encontrar uno rápido, en vez de recorrer toda la lista.',
          },
          {
            num: 2, titulo: 'Hacer clic en "+ NUEVO PRODUCTO"',
            detalle: 'El botón azul está arriba a la derecha de la lista. Se abre una ventana encima de la pantalla con el formulario para crear el producto — no necesitas ir a otra pantalla.',
            imagen: '/manual/productos/02-form-vacio.png',
          },
          {
            num: 3, titulo: 'Completar Código y Nombre',
            detalle: [
              'Código: es el identificador corto que usarán los vendedores para buscar el producto rápido (ej. "ABR-05"). Puede ser el código de barras si lo tienes, o uno que tú inventes.',
              'Nombre: el nombre completo tal como debe aparecer en la factura/boleta del cliente.',
              'Descripción: un detalle opcional, interno, que no sale impreso.',
            ],
            tip: 'Usa códigos cortos y sin espacios. Si dejas el código en blanco algunos sistemas lo autogeneran, pero es mejor ponerle uno tú mismo para no confundirte después.',
          },
          {
            num: 4, titulo: '¿Qué es la Categoría, y por qué es tan importante?',
            detalle: [
              'Categoría es el grupo al que pertenece el producto (ej. "Ferretería", "Pinturas", "Servicios").',
              'Cada categoría tiene una casilla llamada "Afecta Kardex" (la ves y la configuras en Mantenimiento → Categorías, ver la sección siguiente de este manual).',
              'Si la categoría SÍ afecta kardex: cada vez que vendas o compres este producto, el sistema aumenta o descuenta el stock automáticamente.',
              'Si la categoría NO afecta kardex (por ejemplo "Servicios", como una instalación o una revisión): el producto se puede vender igual, pero el sistema nunca le lleva la cuenta de stock — porque no es algo que se "almacene".',
            ],
            peligro: 'Elegir mal la categoría es el error más común y más delicado del sistema: si guardas un producto físico (que sí se debe contar) bajo una categoría de "Servicios", vas a poder venderlo sin límite aunque no tengas stock real, y el Kardex nunca lo va a reflejar. Antes de crear el producto, pregúntate: "¿esto es algo que tengo guardado en un almacén?" Si la respuesta es sí, la categoría debe tener Afecta Kardex = Sí.',
          },
          {
            num: 5, titulo: 'Completar el resto de datos',
            detalle: [
              'Subcategoría, Marca, Modelo, Talla, Color: son datos opcionales para clasificar mejor el producto y facilitar la búsqueda. Puedes dejarlos vacíos si no aplican.',
              'Unidad: la unidad en la que se vende (UNIDAD, CAJA, KILOGRAMO, METRO, etc.). Es obligatoria porque SUNAT exige reportarla en cada comprobante.',
              'Precio: el precio de venta. Puedes escribir el precio con IGV incluido, tal como se lo vas a cobrar al cliente.',
              'Stock Mínimo: la cantidad mínima que quieres tener siempre disponible; sirve solo como referencia/alerta, no bloquea nada.',
            ],
            imagen: '/manual/productos/04-form-lleno.png',
            tip: 'El ejemplo de la imagen usa la categoría "Productos" (que sí afecta kardex) y la unidad "CAJA (BOX)" — así se ve un producto físico normal, listo para vender y comprar con control de stock.',
          },
          {
            num: 6, titulo: 'Foto del producto (opcional)',
            detalle: 'El botón "SUBIR IMAGEN" te permite agregar una foto que aparecerá en el buscador de productos al momento de vender (útil en tiendas con muchos vendedores nuevos, para identificar el producto visualmente).',
          },
          {
            num: 7, titulo: 'Guardar',
            detalle: 'Haz clic en "GUARDAR". El producto aparece de inmediato en la lista, con stock en 0 (todavía no tiene ninguna compra registrada) y queda disponible para usarse en compras y ventas.',
            imagen: '/manual/productos/05-guardado.png',
            nota: 'Un producto recién creado siempre arranca en 0 unidades. Si ya tenías stock físico de este producto antes de usar el sistema, regístralo como una "compra inicial" (ver el módulo de Compras) para que el Kardex arranque con el número correcto.',
          },
        ],
      },
      {
        titulo: 'Categorías — el interruptor "Afecta Kardex"',
        icono: 'Warehouse',
        pasos: [
          {
            num: 1, titulo: 'Ir a Mantenimiento → Categorías',
            detalle: 'Aquí se crean y editan las categorías que luego eliges al crear un producto. La columna "Afecta Kardex" te dice de un vistazo cuáles llevan control de stock y cuáles no.',
            imagen: '/manual/categorias/01-lista.png',
          },
          {
            num: 2, titulo: 'Editar una categoría',
            detalle: 'Haz clic en el lápiz de la fila. Se abre una ventana simple con el nombre y una sola casilla: "Afecta Kardex".',
            imagen: '/manual/categorias/02-afecta-kardex.png',
          },
          {
            num: 3, titulo: '¿Cuándo debe estar marcada?',
            detalle: [
              'Marcada (Sí): para cualquier bien físico que entra y sale de un almacén — mercadería, repuestos, materiales, insumos.',
              'Desmarcada (No): solo para conceptos que NO son bienes físicos — un servicio, una mano de obra, un cargo administrativo.',
            ],
            alerta: 'Cambiar esta casilla afecta a TODOS los productos de esa categoría a la vez, no a uno solo. Si tienes dudas de si un producto en particular debería tener kardex, es más seguro crear una categoría nueva y específica para él que cambiar una categoría que ya tiene muchos productos.',
          },
        ],
      },
      {
        titulo: 'Kardex — Ver movimientos de un producto',
        icono: 'Timeline',
        pasos: [
          { num: 1, titulo: 'Ir al módulo Kardex', detalle: 'En el menú lateral haz clic en "Kardex".' },
          { num: 2, titulo: 'Buscar el producto', detalle: 'Usa el buscador por nombre o código. Selecciona el producto de la lista.' },
          { num: 3, titulo: 'Filtrar por fechas o almacén', detalle: 'Puedes filtrar el historial por rango de fechas y por sucursal/almacén.', tip: 'El Kardex muestra entradas (compras), salidas (ventas) y ajustes, con saldo acumulado en cada movimiento.' },
          { num: 4, titulo: 'Interpretar el kardex', detalle: ['Verde = Entrada (aumenta stock)', 'Rojo = Salida (reduce stock)', 'Amarillo = Ajuste manual', 'La columna "Saldo" muestra el stock en ese momento exacto'] },
        ],
      },
    ],
  },

  // ══════════════════════════════════════════════════════════════
  // COTIZACIONES
  // ══════════════════════════════════════════════════════════════
  {
    id: 'cotizaciones',
    requiereModulo: 'facturacion',
    titulo: 'Cotizaciones',
    descripcion: 'Generar proformas y cotizaciones para clientes, con conversión directa a venta.',
    icono: 'RequestQuote',
    colorHex: '#0277bd',
    categoria: 'comercial',
    secciones: [
      {
        titulo: 'Crear una cotización',
        icono: 'NoteAdd',
        pasos: [
          {
            num: 1, titulo: 'Ir a Facturación → Cotizaciones',
            detalle: 'Se abre la lista de cotizaciones ya creadas, ordenadas de la más reciente a la más antigua, con su estado (Vigente, Convertida, Vencida, Anulada).',
            imagen: '/manual/cotizaciones/01-lista.png',
          },
          {
            num: 2, titulo: 'Clic en "+ NUEVA COTIZACIÓN"',
            detalle: 'El sistema ya trae varios datos listos por defecto, para que no tengas que llenar todo: la sucursal, la serie (ej. C001), el número correlativo siguiente, la moneda (Sol Peruano) y el vendedor (tu propio usuario).',
            imagen: '/manual/cotizaciones/02-form-vacio.png',
            tip: 'Fíjate en el campo "Condición Pago": por defecto viene en "Contado". Si la venta va a ser al crédito, cámbialo aquí antes de guardar.',
          },
          {
            num: 3, titulo: 'Seleccionar el cliente',
            detalle: 'Haz clic en el campo "Cliente *" y elige de la lista, o escribe parte del nombre/RUC/DNI para buscarlo. Si el cliente no existe todavía, hay un botón con un ícono de persona (+) al costado para crearlo sin salir de esta pantalla.',
            imagen: '/manual/cotizaciones/03-cliente.png',
          },
          {
            num: 4, titulo: 'Agregar cada producto',
            detalle: [
              'En la fila de "Detalle de productos", haz clic en el campo "Producto" y busca por nombre o código.',
              'Revisa la Cantidad (por defecto 1) y el Precio (se llena solo con el precio configurado del producto, pero puedes cambiarlo).',
              'Elige el Almacén desde el que saldría la mercadería.',
              'Haz clic en el botón verde "+ AGREGAR" para sumarlo a la lista de abajo.',
              'Repite el proceso para cada producto que lleve la cotización.',
            ],
            imagen: '/manual/cotizaciones/04-con-detalle.png',
            tip: 'La casilla "Precios incluyen IGV" (marcada por defecto) le dice al sistema que el precio que escribiste YA tiene el IGV sumado — así no tienes que calcular tú el 18% a mano.',
          },
          {
            num: 5, titulo: 'Revisar los totales',
            detalle: 'Abajo a la derecha el sistema calcula solo el Subtotal, el IGV y el Total. También puedes ver el campo "A Cuenta" si el cliente adelanta un pago, y "Saldo" con lo que quedaría pendiente.',
            alerta: 'Antes de guardar, confirma que la Moneda (PEN o USD) sea la correcta — no se puede cambiar después de convertida.',
          },
          {
            num: 6, titulo: 'Guardar',
            detalle: 'Haz clic en "GUARDAR COTIZACIÓN". Queda registrada con estado "Vigente" y ya puedes enviarla al cliente por WhatsApp, correo o descargarla en PDF con los íconos de la lista.',
          },
        ],
      },
      {
        titulo: 'Convertir la cotización en una venta real',
        icono: 'SwapHoriz',
        pasos: [
          {
            num: 1, titulo: '¿Por qué convertir en vez de crear todo de nuevo?',
            detalle: 'Cuando el cliente confirma que sí quiere comprar, no hace falta volver a escribir cliente y productos: el sistema toma automáticamente todos los datos de la cotización y arma el documento de venta por ti.',
          },
          {
            num: 2, titulo: 'Clic en el ícono de conversión (⇄)',
            detalle: 'En la lista de cotizaciones, cada fila "Vigente" tiene un ícono con dos flechas en forma de círculo. Solo aparece en cotizaciones que todavía no fueron convertidas ni anuladas.',
            tip: 'Si no ves el ícono en una fila, revisa el estado: si ya dice "Convertida" es porque alguien ya generó su venta antes.',
          },
          {
            num: 3, titulo: 'Elegir en qué se convierte',
            detalle: [
              'Nota de Venta: documento interno, no se envía a SUNAT. Úsalo cuando el cliente no necesita factura ni boleta formal, o para apartar la venta y facturarla después.',
              'Factura: comprobante electrónico (01), solo para clientes con RUC (empresas).',
              'Boleta: comprobante electrónico (03), para personas naturales con o sin DNI.',
            ],
            imagen: '/manual/cotizaciones/06-convertir-menu.png',
          },
          {
            num: 4, titulo: 'Elegir la serie y confirmar',
            detalle: 'El sistema ya te muestra la serie disponible y el siguiente número correlativo. Haz clic en "CONVERTIR".',
            alerta: 'Esta acción no se puede deshacer: la cotización queda marcada como "Convertida" y ya no se puede editar ni volver a convertir.',
          },
          {
            num: 5, titulo: 'Confirmación',
            detalle: 'Aparece un mensaje verde con el número del documento creado (ej. "Nota de Venta N001-00000017 creada exitosamente"), y la cotización cambia a estado "Convertida" en la lista.',
            imagen: '/manual/cotizaciones/07-convertida.png',
            tip: 'Ese nuevo documento ya aparece en su propio módulo (Nota de Venta, Factura o Boleta) con el mismo cliente y los mismos productos — no hay que volver a escribir nada.',
          },
        ],
      },
    ],
  },

  // ══════════════════════════════════════════════════════════════
  // NOTA DE VENTA
  // ══════════════════════════════════════════════════════════════
  {
    id: 'nota_venta',
    requiereModulo: 'facturacion',
    titulo: 'Nota de Venta',
    descripcion: 'Registrar ventas internas que no requieren comprobante electrónico SUNAT.',
    icono: 'Receipt',
    colorHex: '#00695c',
    categoria: 'comercial',
    secciones: [
      {
        titulo: 'Emitir una Nota de Venta',
        icono: 'PointOfSale',
        pasos: [
          {
            num: 1, titulo: 'Ir a Facturación → Nota de Venta',
            detalle: 'Se abre la lista de notas de venta ya emitidas, con su estado: Vigente, Facturada (ya se convirtió en Boleta/Factura) o Anulada.',
            imagen: '/manual/nota_venta/05-lista-directa.png',
            nota: 'La serie de Nota de Venta empieza con "N" (ej. N001) y es un documento 100% interno — nunca se envía a SUNAT.',
          },
          {
            num: 2, titulo: 'Clic en "+ NUEVA NOTA DE VENTA"',
            detalle: 'El formulario es prácticamente igual al de Cotización: sucursal, serie y número ya vienen listos, solo falta elegir el cliente.',
            imagen: '/manual/nota_venta/06-form-vacio.png',
          },
          {
            num: 3, titulo: 'Elegir cliente y agregar productos',
            detalle: 'Busca el cliente por nombre, RUC o DNI. Luego, en "Detalle de productos", elige el producto, la cantidad y el almacén, y haz clic en "+ AGREGAR". El sistema calcula el Subtotal, IGV y Total automáticamente.',
            imagen: '/manual/nota_venta/07-con-detalle.png',
            tip: 'El botón "+ ADICIONAR COTIZACIÓN" te permite traer los productos de una cotización existente en vez de escribirlos de nuevo.',
          },
          {
            num: 4, titulo: 'Forma de pago',
            detalle: ['CONTADO: el cliente paga en el momento.', 'CRÉDITO: activa "Opciones financieras" para definir cuotas y fecha de vencimiento.'],
            tip: 'Si la venta es al crédito, el módulo de Tesorería registra automáticamente la cuenta por cobrar — no hace falta crearla a mano.',
          },
          {
            num: 5, titulo: 'Clic en "GUARDAR NOTA DE VENTA"',
            detalle: 'La nota queda registrada con estado "Vigente". Como es un documento interno, no hay envío a SUNAT ni espera — queda lista al instante.',
            imagen: '/manual/nota_venta/08-resultado.png',
            nota: 'Más adelante, cuando el cliente necesite un comprobante formal, esta misma Nota de Venta se puede convertir en Boleta o Factura — ver la siguiente sección.',
          },
        ],
      },
      {
        titulo: 'Convertir Nota de Venta a comprobante electrónico',
        icono: 'UploadFile',
        pasos: [
          {
            num: 1, titulo: 'Buscar la nota de venta',
            detalle: 'En la lista de Notas de Venta, localiza la que necesitas facturar. Debe estar en estado "Vigente".',
            imagen: '/manual/nota_venta/01-lista.png',
          },
          {
            num: 2, titulo: 'Clic en el ícono de conversión (⇄) de esa fila',
            detalle: 'Se abre una ventana "Emitir Comprobante" donde eliges el tipo: Factura (para clientes con RUC) o Boleta (para personas naturales).',
            imagen: '/manual/nota_venta/03-elegir-boleta.png',
            alerta: 'Para elegir Factura, el cliente de la Nota de Venta debe tener un RUC válido de 11 dígitos cargado. Si el cliente solo tiene DNI, solo vas a poder emitir Boleta.',
          },
          {
            num: 3, titulo: 'Elegir la serie y leer el aviso',
            detalle: 'El sistema muestra la serie disponible (ej. B001) y el siguiente número. El aviso azul te recuerda algo importante: "La Nota de Venta quedará marcada como FACTURADA. El comprobante se enviará a SUNAT automáticamente".',
            nota: 'A diferencia de la Cotización (que se puede convertir en un documento interno o uno electrónico), la Nota de Venta cuando se convierte SIEMPRE termina en un comprobante que va a SUNAT.',
          },
          {
            num: 4, titulo: 'Clic en "EMITIR COMPROBANTE"',
            detalle: 'El sistema genera el XML, lo firma digitalmente con el certificado de la empresa y lo envía a SUNAT. Verás un mensaje verde confirmando el número creado (ej. "Boleta B001-00000031 creada exitosamente") y la Nota de Venta pasa a estado "Facturada".',
            imagen: '/manual/nota_venta/04-convertida.png',
            tip: 'El envío a SUNAT no es instantáneo: puede tardar unos segundos. Revisa el estado del comprobante en su propio módulo (Boleta o Factura) — mientras se procesa dirá "Enviado a SUNAT" antes de confirmar la aceptación final.',
          },
        ],
      },
    ],
  },

  // ══════════════════════════════════════════════════════════════
  // FACTURACIÓN ELECTRÓNICA
  // ══════════════════════════════════════════════════════════════
  {
    id: 'facturacion',
    requiereModulo: 'facturacion',
    titulo: 'Facturación Electrónica',
    descripcion: 'Emitir facturas y boletas electrónicas con validación y envío automático a SUNAT.',
    icono: 'ReceiptLong',
    colorHex: '#1565c0',
    categoria: 'electronico',
    secciones: [
      {
        titulo: 'Emitir una Factura Electrónica',
        icono: 'Assignment',
        pasos: [
          {
            num: 1, titulo: 'Ir a Facturación → Factura',
            detalle: 'Se abre la lista de facturas emitidas, con su estado ante SUNAT en la columna "Estado" (verás varios colores distintos — se explican en la última sección de este módulo).',
            imagen: '/manual/facturacion/02-lista-facturas.png',
          },
          {
            num: 2, titulo: 'Clic en "+ NUEVA FACTURA"',
            detalle: 'El formulario ya trae la sucursal, la serie (ej. F001) y el siguiente número listos. También puedes usar "+ ADICIONAR REFERENCIA" si esta factura corresponde a una Cotización o Nota de Venta previa.',
            imagen: '/manual/facturacion/03-form-vacio.png',
          },
          {
            num: 3, titulo: 'Elegir el cliente — con RUC obligatoriamente',
            detalle: 'Busca por razón social o RUC. A diferencia de la Boleta, la Factura SIEMPRE exige un cliente con RUC de 11 dígitos (una empresa), porque legalmente representa una operación entre negocios.',
            imagen: '/manual/facturacion/04-cliente-elegido.png',
            alerta: 'Si tu cliente es una persona natural sin RUC, no podrás terminar de emitir la factura — usa Boleta en ese caso.',
          },
          {
            num: 4, titulo: 'Agregar productos y ver el stock disponible',
            detalle: 'Al elegir un producto y el almacén, el sistema muestra un aviso verde "Stock disponible: X" para que sepas cuánto hay antes de vender. Escribe la Cantidad y haz clic en "+ AGREGAR".',
            imagen: '/manual/facturacion/05-stock-disponible.png',
            alerta: 'Si la cantidad supera el stock disponible, el sistema no deja guardar la factura — esto evita vender algo que no existe en el almacén.',
          },
          {
            num: 5, titulo: 'Revisar Condición de Pago e impuestos',
            detalle: [
              'CONTADO: el cliente paga en el momento.',
              'CRÉDITO: puedes activar "Opciones financieras" para definir cuotas y fechas de vencimiento.',
              'El "Tipo IGV" de cada línea (Gravada, Inafecta, Exonerada) ya viene configurado desde el producto — normalmente no hace falta tocarlo.',
            ],
          },
          {
            num: 6, titulo: 'Clic en "GUARDAR FACTURA"',
            detalle: 'El sistema genera el XML, lo firma digitalmente con el certificado de la empresa y lo envía a SUNAT automáticamente. No hay un paso separado de "enviar" — sucede solo al guardar.',
          },
          {
            num: 7, titulo: 'Descargar el PDF',
            detalle: 'Desde la lista, el ícono de documento (azul) descarga el PDF para entregárselo al cliente. El XML firmado queda guardado en el sistema para tus archivos contables.',
          },
        ],
      },
      {
        titulo: 'Emitir una Boleta de Venta',
        icono: 'ConfirmationNumber',
        pasos: [
          {
            num: 1, titulo: 'Ir a Facturación → Boleta',
            detalle: 'La lista funciona igual que la de Factura. El formulario de creación también es prácticamente idéntico.',
            imagen: '/manual/facturacion/07-lista-boletas.png',
          },
          {
            num: 2, titulo: 'Clic en "+ NUEVA BOLETA"',
            detalle: 'Igual que Factura: sucursal, serie (ej. B001) y número ya vienen listos.',
            imagen: '/manual/facturacion/08-boleta-form-vacio.png',
          },
          {
            num: 3, titulo: 'Identificar al cliente',
            detalle: ['Con DNI: ingresa el número o busca por nombre, el sistema ya trae el dato guardado.', 'Sin documento: para montos bajos, algunos negocios usan un cliente genérico "Cliente Varios".', 'Con RUC: una boleta también se le puede emitir a una empresa, a diferencia de la Factura que exige RUC obligatoriamente.'],
            imagen: '/manual/facturacion/09-boleta-cliente-dni.png',
            tip: 'Para ventas menores a S/ 700, SUNAT no exige identificar al comprador con documento.',
          },
          {
            num: 4, titulo: 'Agregar productos',
            detalle: 'Mismo proceso que la Factura: elige el producto, la cantidad, el almacén y haz clic en "+ AGREGAR". Revisa los totales calculados abajo.',
            imagen: '/manual/facturacion/10-boleta-con-detalle.png',
          },
          {
            num: 5, titulo: 'Clic en "GUARDAR"',
            detalle: 'Al guardar, el comprobante se firma y se envía a SUNAT automáticamente — igual que una Factura, solo que la Boleta no exige RUC del cliente.',
            imagen: '/manual/facturacion/11-boleta-resultado.png',
          },
        ],
      },
      {
        titulo: 'Entender los estados de un comprobante',
        icono: 'CloudSync',
        pasos: [
          {
            num: 1, titulo: 'Los estados que vas a ver en la lista',
            detalle: [
              'EN_COLA: el comprobante se acaba de crear y está esperando su turno para enviarse a SUNAT — normalmente dura segundos.',
              'Enviado a SUNAT (naranja): ya se envió, pero el sistema todavía está esperando la confirmación final de SUNAT.',
              'Aceptado (verde): SUNAT confirmó y validó el comprobante. Este es el estado final correcto — es el que debe tener toda venta cerrada.',
              'ERROR_ENVIO (rojo): algo falló al enviarlo — puede ser un problema de datos o una demora del propio SUNAT. El ícono naranja de "reintentar" (una flecha circular) vuelve a intentar el envío.',
            ],
            imagen: '/manual/facturacion/06-resultado-estados.png',
            tip: 'Es normal ver varios estados distintos en la misma lista al mismo tiempo — cada comprobante avanza a su propio ritmo.',
          },
          {
            num: 2, titulo: '¿Qué hago si queda en ERROR_ENVIO?',
            detalle: 'Primero, dale clic al ícono de reintentar — muchas veces el error es una demora temporal de SUNAT y se resuelve solo. Si el error persiste, revisa el detalle del mensaje: casi siempre indica exactamente qué dato está mal (RUC inválido, unidad de medida incorrecta, dirección incompleta, etc.).',
            alerta: 'Nunca "inventes" un dato para que pase el error (por ejemplo, un RUC cualquiera) — corrígelo con el dato real del cliente o el producto.',
          },
        ],
      },
      {
        titulo: 'Venta Rápida: vender en segundos',
        icono: 'PointOfSale',
        pasos: [
          {
            num: 1, titulo: '¿Para qué sirve Venta Rápida?',
            detalle: 'Es una pantalla única pensada para mostrador: en vez de llenar un formulario paso a paso, buscas el producto, lo tocas para agregarlo a un carrito, y confirmas. Con el mismo carrito puedes emitir una Nota de Venta, una Boleta o una Factura — tú eliges cuál, sin cambiar de pantalla.',
            imagen: '/manual/venta_rapida/01-inicial.png',
            tip: 'Es ideal cuando atiendes varias ventas seguidas de pocos ítems y quieres ser más rápido que con el formulario completo.',
          },
          {
            num: 2, titulo: 'Ir a Facturación → Venta Rápida',
            detalle: 'Arriba eliges qué vas a emitir — N.Venta, Boleta o Factura — y el formato de impresión (T80 para ticketera, A5 o A4). La Serie y el Almacén se ajustan solos según el tipo elegido; revisa que sean los correctos antes de vender.',
          },
          {
            num: 3, titulo: 'Buscar y agregar productos',
            detalle: 'Escribe en "Buscar producto" (por nombre o código) y aparecen tarjetas con el precio y el stock de cada uno. Un solo clic sobre la tarjeta lo agrega al carrito de la derecha — no hace falta ningún botón extra.',
            imagen: '/manual/venta_rapida/02-buscar.png',
            tip: 'El campo "Código (F8)" está pensado para un lector de código de barras: escanea el producto y se agrega directo al carrito.',
          },
          {
            num: 4, titulo: 'Revisar el carrito y elegir al cliente',
            detalle: [
              'En cada línea del carrito puedes ajustar la cantidad con los botones + / −, o escribir el precio si necesitas cambiarlo.',
              'El campo "Cliente" es obligatorio para los 3 tipos de documento. Si el cliente no existe, créalo ahí mismo sin salir de la pantalla.',
              'El interruptor "Precios incl. IGV" define si el precio que ves en cada tarjeta ya trae el IGV incluido o no.',
            ],
            imagen: '/manual/venta_rapida/03-carrito.png',
          },
          {
            num: 5, titulo: 'Confirmar la venta',
            detalle: 'El botón grande de abajo cambia de nombre según el tipo elegido (por ejemplo "CONFIRMAR BOLETA"). Antes de guardar se muestra un resumen con el cliente, la cantidad de productos y el total a cobrar, para una última revisión.',
            imagen: '/manual/venta_rapida/04-confirmar.png',
          },
          {
            num: 6, titulo: 'Listo: el documento queda emitido',
            detalle: 'Aparece un aviso de confirmación con el número del documento y un acceso directo "VER PDF". El carrito se vacía solo, quedando lista para la siguiente venta de inmediato.',
            imagen: '/manual/venta_rapida/05-resultado.png',
            nota: 'Si elegiste Boleta o Factura, el envío a SUNAT sigue el mismo proceso y los mismos estados (EN_COLA, Enviado, Aceptado) que ya viste en esta sección.',
          },
        ],
      },
    ],
  },

  // ══════════════════════════════════════════════════════════════
  // NOTA DE CRÉDITO
  // ══════════════════════════════════════════════════════════════
  {
    id: 'nota_credito',
    requiereModulo: 'facturacion',
    titulo: 'Nota de Crédito',
    descripcion: 'Anular, devolver o descontar montos de comprobantes electrónicos ya emitidos.',
    icono: 'CreditScore',
    colorHex: '#6a1b9a',
    categoria: 'electronico',
    secciones: [
      {
        titulo: '¿Cuándo usar una Nota de Crédito?',
        icono: 'Help',
        pasos: [
          { num: 1, titulo: 'Anulación total', detalle: 'Cuando el comprobante fue emitido con error grave y necesitas anularlo completamente. La NC cancela el 100% del monto original.' },
          { num: 2, titulo: 'Devolución parcial', detalle: 'El cliente devuelve parte de los productos. La NC reduce el monto por los ítems devueltos.' },
          { num: 3, titulo: 'Descuento posterior', detalle: 'Se ofrece un descuento al cliente después de emitido el comprobante. La NC registra el descuento.', nota: 'La Nota de Crédito siempre debe hacer referencia al comprobante original (número y serie).' },
        ],
      },
      {
        titulo: 'Emitir una Nota de Crédito',
        icono: 'PostAdd',
        pasos: [
          {
            num: 1, titulo: 'Ir a Facturación → Nota de Crédito',
            detalle: 'Verás la lista de NC ya emitidas, cada una mostrando a qué factura o boleta corrige (ej. "Corrige Factura F001-00000005").',
            imagen: '/manual/nota_credito/01-lista.png',
          },
          {
            num: 2, titulo: 'Clic en "+ NUEVA NOTA DE CRÉDITO" y luego en "REFERENCIA"',
            detalle: 'No se escribe el número del comprobante a mano: el botón naranja "REFERENCIA" abre un buscador donde eliges el documento exacto de una lista, con su cliente, fecha y total — así no hay riesgo de equivocarte de comprobante.',
            imagen: '/manual/nota_credito/02-referencia-picker.png',
            tip: 'Puedes cambiar la pestaña "Tipo Documento" arriba del buscador si el comprobante a corregir es una Boleta en vez de una Factura.',
          },
          {
            num: 3, titulo: 'Todo se carga automático',
            detalle: 'Al hacer clic en "SELECCIONAR", el sistema completa solo: el cliente, el número de comprobante de referencia, y — a diferencia de la Nota de Débito — también copia los productos de esa venta en el detalle. Solo falta elegir el "Tipo Nota Crédito" (el motivo) y escribir el "Motivo Nota" en texto.',
            imagen: '/manual/nota_credito/03-form-completo.png',
          },
          {
            num: 4, titulo: 'Elegir el motivo correcto',
            detalle: ['01 - Anulación de la operación: cancela el 100% del comprobante.', '02 - Anulación por error en el RUC: se emitió a la empresa equivocada.', '03 - Corrección por error en la descripción.', '04/05 - Descuento global o por ítem.', '06/07 - Devolución total o por ítem: el cliente regresó la mercadería.', '13 - Ajustes de operaciones de exportación.'], alerta: 'Solo puedes emitir una NC sobre comprobantes que SUNAT ya aceptó. Si el comprobante original quedó en error o rechazado, no corresponde una NC — hay que corregirlo o anularlo directamente.' },
          {
            num: 5, titulo: 'Ajustar cantidades si es una devolución parcial',
            detalle: 'Si el motivo es una devolución o descuento PARCIAL (no total), edita las cantidades o montos de los productos cargados antes de guardar, para que la NC represente exactamente lo que se está corrigiendo.',
          },
          {
            num: 6, titulo: 'Guardar',
            detalle: 'El sistema genera el XML, lo firma y lo envía a SUNAT. La nueva NC queda enlazada al comprobante original para siempre — cualquiera que revise la factura original va a ver que tiene una NC asociada.',
            imagen: '/manual/nota_credito/04-resultado.png',
          },
        ],
      },
    ],
  },

  // ══════════════════════════════════════════════════════════════
  // NOTA DE DÉBITO
  // ══════════════════════════════════════════════════════════════
  {
    id: 'nota_debito',
    requiereModulo: 'facturacion',
    titulo: 'Nota de Débito',
    descripcion: 'Incrementar el monto de un comprobante electrónico ya emitido.',
    icono: 'AddCard',
    colorHex: '#c62828',
    categoria: 'electronico',
    secciones: [
      {
        titulo: '¿Cuándo usar una Nota de Débito?',
        icono: 'Help',
        pasos: [
          { num: 1, titulo: 'Penalidades o intereses', detalle: 'Cuando el cliente tiene deuda vencida y corresponde cobrar intereses moratorios.' },
          { num: 2, titulo: 'Cargos adicionales', detalle: 'Se añaden costos extra no incluidos en la factura original (ej. flete, embalaje adicional).', nota: 'La Nota de Débito AUMENTA el monto del comprobante original. Para REDUCIR usa Nota de Crédito.' },
        ],
      },
      {
        titulo: 'Emitir una Nota de Débito',
        icono: 'PostAdd',
        pasos: [
          {
            num: 1, titulo: 'Ir a Facturación → Nota de Débito',
            detalle: 'La lista funciona igual que la de Nota de Crédito, mostrando a qué comprobante corrige cada una.',
            imagen: '/manual/nota_debito/01-lista.png',
          },
          {
            num: 2, titulo: 'Clic en "+ NUEVA NOTA DE DÉBITO" y en "REFERENCIA"',
            detalle: 'Igual que en la Nota de Crédito, eliges el comprobante original de una lista en vez de escribir el número a mano.',
            imagen: '/manual/nota_debito/03-form-vacio.png',
          },
          {
            num: 3, titulo: 'Elegir el motivo y escribir por qué',
            detalle: ['01 - Intereses por mora: el cliente pagó tarde.', '02 - Aumento en el valor: se corrige un precio que quedó muy bajo.', '03 - Penalidades: cargos por incumplimiento de contrato.'],
            nota: 'Recuerda: la Nota de Débito SIEMPRE aumenta lo que el cliente debe. Si en cambio necesitas reducir un monto, es una Nota de Crédito, no de Débito.',
          },
          {
            num: 4, titulo: 'Diferencia clave con la Nota de Crédito: los productos NO se copian solos',
            detalle: 'El sistema completa el cliente y el número de referencia, pero el detalle de productos queda vacío — porque una Nota de Débito casi siempre es un cargo NUEVO (un interés, un flete) que no estaba en la venta original, así que tienes que agregarlo tú mismo en "Detalle de productos".',
            imagen: '/manual/nota_debito/04-form-completo-detalle.png',
            tip: 'Puedes usar un "producto" que represente el concepto del cargo (ej. "Interés por mora", "Flete adicional") en vez de un producto físico del inventario.',
          },
          {
            num: 5, titulo: 'Clic en "GUARDAR NOTA DE DÉBITO"',
            detalle: 'El sistema genera el XML, lo firma y lo envía a SUNAT. Queda vinculada al comprobante original, visible en la lista como "Corrige Factura ...".',
            imagen: '/manual/nota_debito/05-resultado.png',
            alerta: 'Si ves un aviso de "Número de correlativo duplicado", es una protección del sistema contra dobles clics o series desincronizadas — solo acepta "Regularizar serie" y continúa, nunca fuerces el mismo número dos veces.',
          },
        ],
      },
    ],
  },

  // ══════════════════════════════════════════════════════════════
  // GUÍA DE REMISIÓN
  // ══════════════════════════════════════════════════════════════
  {
    id: 'guia_remision',
    requiereModulo: 'facturacion',
    titulo: 'Guía de Remisión',
    descripcion: 'Emitir guías de remisión para traslado de bienes — remitente y transportista.',
    icono: 'LocalShipping',
    colorHex: '#e65100',
    categoria: 'electronico',
    secciones: [
      {
        titulo: 'Guía del Remitente (tu empresa traslada)',
        icono: 'Warehouse',
        pasos: [
          {
            num: 1, titulo: '¿Cuándo se necesita una guía?',
            detalle: 'Cada vez que sacas mercadería física de tu almacén para llevarla a otro lugar (al cliente, a otra sucursal, a un evento) por ley debe viajar acompañada de una Guía de Remisión. No es opcional: si te fiscalizan en la carretera sin guía, hay multa.',
          },
          {
            num: 2, titulo: 'Ir a Facturación → Guía de Remisión Remitente',
            detalle: 'Se abre la lista de guías ya emitidas, con su estado ante SUNAT (EN_COLA, ENVIADO, ERROR_ENVIO, etc.) en la columna "Estado CPE".',
            imagen: '/manual/guia_remision/01-lista.png',
          },
          {
            num: 3, titulo: 'Clic en "+ NUEVA GUÍA"',
            detalle: 'El formulario ya trae varios datos por defecto: sucursal, serie (T001), fecha de emisión, moneda y vendedor. Los campos con asterisco (*) son obligatorios.',
            imagen: '/manual/guia_remision/02-form-vacio.png',
          },
          {
            num: 4, titulo: 'La forma más rápida: "CARGAR DESDE DOCUMENTO"',
            detalle: 'En vez de escribir el cliente y los productos a mano, baja hasta la sección "Bienes a Transportar" y haz clic en "CARGAR DESDE DOCUMENTO". Se abre una ventana donde eliges entre las pestañas "Cotización" o "Nota de Venta" y seleccionas el documento que originó esta venta.',
            imagen: '/manual/guia_remision/03-cargar-documento.png',
            tip: 'Esto evita errores de digitación y asegura que la guía transporte exactamente lo que el cliente compró.',
          },
          {
            num: 5, titulo: 'Revisar los productos cargados',
            detalle: 'Los productos de esa venta aparecen automáticamente en la tabla "Bienes a Transportar", con su cantidad y almacén de origen.',
            imagen: '/manual/guia_remision/04-items-cargados.png',
            alerta: 'Cargar los productos NO llena el Cliente/Destinatario ni la Dirección de Llegada — esos campos se completan aparte, ver el siguiente paso.',
          },
          {
            num: 6, titulo: 'Completar los datos obligatorios restantes',
            detalle: [
              'Cliente / Destinatario: a quién le llega la mercadería.',
              'Motivo de Traslado: normalmente "01 - Venta".',
              'Peso Bruto (KGM): el peso total de lo que se transporta. El sistema no deja guardar si está en 0.',
              'Dirección Llegada: adónde va la mercadería.',
              'Placa Vehículo, Nro. Doc. Chofer, Nombres, Apellidos y Licencia: datos de quién maneja.',
            ],
            imagen: '/manual/guia_remision/05-form-completo.png',
            tip: 'Si usas siempre el mismo vehículo o chofer, regístralos una vez en Mantenimiento → Vehículos / Choferes: así después solo los seleccionas del "catálogo" en vez de escribirlos cada vez.',
          },
          {
            num: 7, titulo: 'Guardar y emitir',
            detalle: 'Haz clic en "GUARDAR". El sistema genera el XML, lo firma con el certificado digital y lo envía a SUNAT — verás el nuevo registro con estado "EN_COLA" mientras se procesa el envío.',
            imagen: '/manual/guia_remision/06-resultado.png',
            nota: 'Descarga o imprime el PDF de la guía y entrégaselo al conductor: debe llevarlo físicamente (o en su celular) durante todo el traslado.',
          },
        ],
      },
      {
        titulo: 'Guía del Transportista (empresa de transporte)',
        icono: 'DirectionsBus',
        pasos: [
          {
            num: 1, titulo: '¿En qué se diferencia de la Guía Remitente?',
            detalle: 'La Guía Remitente la emite el DUEÑO de la mercadería (quien la envía). La Guía Transportista la emite la EMPRESA DE TRANSPORTE que presta el servicio de llevarla. Si tu empresa solo vende y usa una empresa de transporte externa, normalmente solo te preocupa la guía Remitente — esta sección es para cuando tu empresa ES el transportista.',
            nota: 'Una misma operación puede terminar con DOS guías electrónicas: una Remitente y otra Transportista, cada una emitida por una empresa distinta.',
          },
          {
            num: 2, titulo: 'Ir a Facturación → Guía de Remisión Transportista',
            detalle: 'El sistema muestra un aviso "Modo Transportista (31)" para que sepas que estás en el formulario correcto — es distinto al de la guía remitente aunque se ve parecido.',
            imagen: '/manual/guia_transportista/02-form-vacio.png',
          },
          {
            num: 3, titulo: 'La diferencia en el formulario',
            detalle: [
              'Cliente / Destinatario: quién recibe la mercadería (igual que en la remitente).',
              'Datos de Transporte: aquí, en vez de pedir el vehículo/chofer directamente, pide el RUC y Razón Social del Transportista — porque este documento es EMITIDO por esa empresa de transporte sobre sí misma.',
              'Tipo Transporte queda fijo en "Público", porque por definición una empresa de transporte presta el servicio a terceros.',
            ],
          },
          {
            num: 4, titulo: 'Completar y guardar',
            detalle: 'El resto del formulario (motivo de traslado, puntos de partida/llegada, bienes a transportar) funciona igual que en la Guía Remitente. El PDF resultante es obligatorio para el conductor durante todo el traslado.',
          },
        ],
      },
    ],
  },

  // ══════════════════════════════════════════════════════════════
  // ANULACIONES
  // ══════════════════════════════════════════════════════════════
  {
    id: 'anulaciones',
    requiereModulo: 'facturacion',
    titulo: 'Anulación de Comprobantes',
    descripcion: 'Anular boletas mediante Resumen Diario y facturas mediante Comunicación de Baja.',
    icono: 'Cancel',
    colorHex: '#b71c1c',
    categoria: 'electronico',
    secciones: [
      {
        titulo: 'Anular una Boleta de Venta',
        icono: 'RemoveCircle',
        pasos: [
          { num: 1, titulo: 'Buscar la boleta', detalle: 'En la lista de boletas, localiza el comprobante a anular. Solo se puede anular un comprobante que SUNAT ya aceptó.' },
          {
            num: 2, titulo: 'Clic en el ícono de anular (el "borrador tachado" rojo) de esa fila',
            detalle: 'Se abre una ventana de confirmación con los datos del comprobante (número, cliente, total, fecha) y una advertencia en naranja: la anulación se reporta a SUNAT y NO puede deshacerse.',
            imagen: '/manual/anulaciones/01-confirmar.png',
            peligro: 'Lee bien el comprobante antes de confirmar — una vez enviada la anulación a SUNAT, no hay forma de revertirla desde el sistema.',
          },
          {
            num: 3, titulo: 'Escribir el motivo',
            detalle: 'El campo "Motivo de Anulación" es obligatorio y debe tener entre 6 y 120 caracteres — explica brevemente por qué se anula (ej. "Error en el monto, se re-emite correctamente").',
          },
          { num: 4, titulo: 'Clic en "PROCEDER CON ANULACIÓN"', detalle: 'El sistema genera automáticamente un Resumen Diario (RC) para reportar la anulación a SUNAT.' },
          { num: 5, titulo: 'Proceso asíncrono — hay que esperar', detalle: 'SUNAT procesa la anulación de boletas de forma asíncrona: el sistema obtiene un ticket y consulta el resultado automáticamente cada cierto tiempo.', alerta: 'La anulación de boletas puede tardar varias horas en confirmarse durante días hábiles — no es instantánea como emitir un comprobante nuevo.' },
          { num: 6, titulo: 'Verificar resultado', detalle: 'Cuando SUNAT confirme, el estado de la boleta cambiará a "ANULADO". Puedes revisar el progreso en el historial del comprobante.' },
        ],
      },
      {
        titulo: 'Anular una Factura',
        icono: 'RemoveCircle',
        pasos: [
          { num: 1, titulo: 'Solo dentro del día', detalle: 'Una factura solo puede anularse mediante Comunicación de Baja si aún no ha sido aceptada o en el mismo día de emisión.', peligro: 'Si la factura ya fue aceptada y pasó el día, debes emitir una Nota de Crédito en lugar de anularla.' },
          {
            num: 2, titulo: 'El proceso es idéntico al de la Boleta',
            detalle: 'Mismo ícono, misma ventana de confirmación con el motivo obligatorio, mismo aviso de que no se puede deshacer. SUNAT procesa la anulación mediante un ticket.',
            imagen: '/manual/anulaciones/01-confirmar.png',
          },
        ],
      },
    ],
  },

  // ══════════════════════════════════════════════════════════════
  // COMPRAS
  // ══════════════════════════════════════════════════════════════
  {
    id: 'compras',
    requiereModulo: 'logistica',
    titulo: 'Compras',
    descripcion: 'Registrar compras de proveedores, gestionar cuentas por pagar y actualizar inventario.',
    icono: 'ShoppingCart',
    colorHex: '#1b5e20',
    categoria: 'comercial',
    secciones: [
      {
        titulo: 'Registrar una compra',
        icono: 'AddShoppingCart',
        pasos: [
          {
            num: 1, titulo: '¿Para qué sirve registrar una compra?',
            detalle: 'Cada vez que le compras mercadería a un proveedor, registrarla aquí hace dos cosas a la vez: guarda el gasto para tu contabilidad y AUMENTA el stock del producto en el almacén que elijas. Es la forma normal de que el stock de un producto "arranque" o "se reponga".',
          },
          {
            num: 2, titulo: 'Ir a Logística → Registro de Compras',
            detalle: 'Se abre la lista de compras ya registradas: fecha, tipo de comprobante, proveedor, almacén y total.',
            imagen: '/manual/compras/01-lista.png',
          },
          {
            num: 3, titulo: 'Clic en "+ NUEVA COMPRA"',
            detalle: 'El formulario está dividido en 3 partes claras: "Proveedor y Comprobante", "Logística y Condiciones" y "Detalle de Productos".',
            imagen: '/manual/compras/02-form-vacio.png',
          },
          {
            num: 4, titulo: 'Elegir el proveedor',
            detalle: 'Haz clic en el campo "Proveedor *" y búscalo por nombre o RUC. Si es la primera vez que le compras a este proveedor, usa el botón con el ícono de "+persona" al costado para registrarlo sin salir de la pantalla.',
            imagen: '/manual/compras/03-proveedor.png',
          },
          {
            num: 5, titulo: 'Datos del comprobante que te dio el proveedor',
            detalle: [
              'Tipo Comprobante: Factura, Boleta, Ticket, etc. — el que el proveedor realmente te entregó.',
              'Serie y Número: tal como aparecen impresos en ese comprobante.',
              'Fecha Emisión y Fecha Vencimiento (si es a crédito).',
            ],
            nota: 'Estos datos son los del documento FÍSICO/electrónico que recibiste, no se inventan — deben coincidir con el papel o PDF que te dio el proveedor, porque es lo que sustenta tu compra ante SUNAT.',
          },
          {
            num: 6, titulo: 'Elegir el Almacén de ingreso',
            detalle: 'Aquí decides a qué almacén entra físicamente la mercadería. Es un campo obligatorio (*): si tienes más de un almacén, asegúrate de elegir el correcto, porque el stock solo sube en ESE almacén, no en todos.',
          },
          {
            num: 7, titulo: 'Agregar cada producto comprado',
            detalle: [
              'En la fila de "Detalle de Productos", haz clic en "Buscar producto..." y elige el producto (o créalo ahí mismo si es nuevo).',
              'Escribe la Cantidad y el Costo Unitario (el precio al que te lo vendió el proveedor, sin incluir IGV).',
              'El sistema calcula el Subtotal, el IGV (18% por defecto) y el Total de esa línea automáticamente.',
            ],
            imagen: '/manual/compras/04-producto.png',
            tip: 'El interruptor "Precios c/ IGV" arriba de la tabla cambia si el costo que escribes ya incluye IGV o no — actívalo solo si el proveedor te dio el precio final con impuesto incluido.',
          },
          {
            num: 8, titulo: 'Revisar los totales antes de guardar',
            detalle: 'Abajo a la derecha aparecen el Subtotal (sin IGV), el IGV y el TOTAL final de toda la compra, sumando todas las líneas agregadas.',
            imagen: '/manual/compras/05-form-lleno.png',
          },
          {
            num: 9, titulo: 'Adjuntar el comprobante escaneado (opcional pero recomendado)',
            detalle: 'El botón "ADJUNTAR FACTURA/BOLETA ESCANEADA" te permite subir una foto o PDF del comprobante físico, para tenerlo respaldado dentro del sistema.',
          },
          {
            num: 10, titulo: 'Clic en "REGISTRAR COMPRA"',
            detalle: 'La compra queda guardada, aparece en la lista, y el stock del almacén elegido sube automáticamente con las cantidades ingresadas.',
            imagen: '/manual/compras/06-resultado.png',
            tip: 'Si la compra es a crédito, revisa el módulo de Tesorería: ahí se genera sola la cuenta por pagar a ese proveedor, con su fecha de vencimiento.',
          },
        ],
      },
    ],
  },

  // ══════════════════════════════════════════════════════════════
  // TESORERÍA
  // ══════════════════════════════════════════════════════════════
  {
    id: 'tesoreria',
    requiereModulo: 'tesoreria',
    titulo: 'Tesorería',
    descripcion: 'Gestionar cuentas por cobrar y pagar, cancelaciones y flujo de caja.',
    icono: 'AccountBalance',
    colorHex: '#01579b',
    categoria: 'finanzas',
    secciones: [
      {
        titulo: 'Ver y cobrar cuentas por cobrar',
        icono: 'TrendingUp',
        pasos: [
          {
            num: 1, titulo: 'Ir a Tesorería',
            detalle: 'La pantalla arranca en la pestaña "COBROS (VENTAS)" y muestra 4 tarjetas resumen arriba: Cobros Pendientes, Pagos Pendientes, Cobrado hoy y el Balance general. Debajo, la lista completa de documentos con su Total, lo Cancelado y lo Pendiente.',
            imagen: '/manual/tesoreria/01-cobros.png',
            tip: 'No solo aparecen Facturas y Boletas: también Notas de Venta — cualquier documento con un monto por cobrar entra aquí automáticamente al guardarse.',
          },
          {
            num: 2, titulo: 'Identificar qué falta cobrar',
            detalle: 'En la columna "Estado" verás: CANCELADO (ya se pagó todo), PENDIENTE (falta cobrar) o ANULADO (el documento fue anulado, ya no aplica). El botón verde con el ícono de tarjeta en la columna "Acciones" solo aparece en las filas PENDIENTE.',
          },
          {
            num: 3, titulo: 'Registrar el cobro',
            detalle: 'Al hacer clic en el ícono verde se abre "Registrar Cobro" con los datos del documento ya cargados: cliente, total y lo pendiente. Completa el Monto a cancelar, el Medio de Pago (efectivo, transferencia, etc.) y la Cuenta bancaria si aplica.',
            imagen: '/manual/tesoreria/02-registrar-cobro.png',
            tip: 'Si el cliente paga solo una parte, cambia el "Monto a cancelar" a esa cantidad — el documento queda como pendiente por la diferencia, no hace falta hacer nada más.',
          },
          {
            num: 4, titulo: 'Confirmar',
            detalle: 'Clic en "CONFIRMAR COBRO". El documento pasa a CANCELADO (o baja su monto pendiente si fue parcial) y el dinero queda reflejado en el Balance general.',
          },
        ],
      },
      {
        titulo: 'Ver cuentas por pagar',
        icono: 'TrendingDown',
        pasos: [
          { num: 1, titulo: 'Pestaña "PAGOS (COMPRAS)"', detalle: 'Lista todas las compras a crédito pendientes de pago a tus proveedores, con la misma estructura que la de cobros.' },
          { num: 2, titulo: 'Registrar un pago', detalle: 'Mismo proceso que un cobro: clic en el ícono de la fila, ingresa el monto pagado y el medio de pago.' },
          { num: 3, titulo: 'Cuentas bancarias', detalle: 'La pestaña "CUENTAS" te deja registrar tus cuentas bancarias, para que cada cobro/pago pueda indicar de qué banco entró o salió el dinero.', tip: 'Registrar la cuenta bancaria en cada movimiento es lo que permite que el sistema calcule el saldo real de cada banco por separado, no solo un total genérico.' },
        ],
      },
    ],
  },

  // ══════════════════════════════════════════════════════════════
  // CAJA CHICA
  // ══════════════════════════════════════════════════════════════
  {
    id: 'caja_chica',
    requiereModulo: 'cajaChica',
    titulo: 'Caja Chica',
    descripcion: 'Administrar fondos rotativos de efectivo para gastos menores del día a día.',
    icono: 'LocalAtm',
    colorHex: '#e65100',
    categoria: 'finanzas',
    secciones: [
      {
        titulo: 'Crear y abrir una Caja Chica',
        icono: 'AddCard',
        pasos: [
          {
            num: 1, titulo: 'Ir a Caja Chica',
            detalle: 'Arriba ves 4 indicadores generales: Cajas Activas, Total en Caja (de todas juntas), Nivel Promedio y Alertas de Reposición. Debajo, una tarjeta por cada caja con su barra de progreso (verde = saldo saludable, naranja/rojo = bajo).',
            imagen: '/manual/caja_chica/01-inicio.png',
            tip: 'Los filtros "Activas / Cerradas / Todas" te ayudan a ocultar cajas que ya no se usan sin perder su historial.',
          },
          { num: 2, titulo: 'Clic en "+ NUEVA CAJA"', detalle: 'Completa: nombre (ej. "Caja Oficina"), responsable, fondo asignado y fecha de apertura.' },
          { num: 3, titulo: 'Umbral de reposición', detalle: 'Configura un monto mínimo (ej. S/ 50). Cuando el saldo baje de ese umbral, aparece una alerta automática en el contador "Alertas de Reposición" de arriba.', tip: 'Un umbral de reposición equivale al 20-30% del fondo asignado es lo más recomendable.' },
          { num: 4, titulo: 'La caja queda activa', detalle: 'Se registra automáticamente el movimiento de "Apertura" con el fondo inicial, visible en el historial de movimientos.' },
        ],
      },
      {
        titulo: 'Registrar un gasto (Egreso)',
        icono: 'ArrowDownward',
        pasos: [
          {
            num: 1, titulo: 'Seleccionar la caja',
            detalle: 'Haz clic en una caja activa de la lista izquierda. A la derecha se abre su detalle: el Saldo Actual en grande, los botones de acción y el historial completo de Movimientos (fecha, tipo, concepto, monto y saldo resultante).',
            imagen: '/manual/caja_chica/02-detalle.png',
          },
          { num: 2, titulo: 'Clic en "REGISTRAR EGRESO" (botón rojo)', detalle: 'Ingresa el monto, el concepto (ej. "Compra de útiles") y la categoría del gasto.', tip: 'Guarda siempre el comprobante físico (boleta, ticket) que respalda cada gasto de caja chica, aunque el sistema no te obligue a adjuntarlo.' },
          { num: 3, titulo: 'El saldo se actualiza al instante', detalle: 'El nuevo movimiento "Egreso" aparece arriba de la tabla, en rojo, y el "Saldo Actual" y la barra de progreso bajan automáticamente.' },
        ],
      },
      {
        titulo: 'Reponer el fondo',
        icono: 'ArrowUpward',
        pasos: [
          {
            num: 1, titulo: 'Clic en "REPONER FONDO" (botón verde)',
            detalle: 'Cuando el saldo baja mucho, este botón agrega efectivo nuevo a la caja. El diálogo te recuerda arriba el Saldo actual y el Fondo asignado, para que sepas cuánto falta para completarlo.',
            imagen: '/manual/caja_chica/03-reponer-fondo.png',
          },
          { num: 2, titulo: 'Ingresar el monto y el concepto', detalle: 'Escribe cuánto efectivo se añade (por defecto sugiere el concepto "Reposición de caja chica", pero puedes cambiarlo) y la fecha.' },
          { num: 3, titulo: 'Clic en "REPONER"', detalle: 'El movimiento queda registrado como "Reposición" (en verde) en el historial, y el Saldo Actual sube de inmediato.' },
        ],
      },
      {
        titulo: 'Realizar un Arqueo',
        icono: 'Balance',
        pasos: [
          { num: 1, titulo: '¿Qué es un arqueo?', detalle: 'Es un conteo físico del efectivo real en la caja (contar billetes y monedas a mano) para verificar que coincide con el saldo que dice el sistema.' },
          {
            num: 2, titulo: 'Clic en "ARQUEO"',
            detalle: 'El sistema te muestra el "Saldo contable en sistema" y te pide el "Saldo Físico Contado" — lo que realmente contaste. Abajo calcula la "Diferencia" al instante, en verde si es S/ 0.00 o en otro color si no cuadra.',
            imagen: '/manual/caja_chica/04-arqueo.png',
          },
          { num: 3, titulo: 'Anotar observaciones si hay diferencia', detalle: 'Si el efectivo contado no coincide con el sistema, describe el motivo probable (billete contado dos veces, gasto sin comprobante todavía registrado, etc.) antes de confirmar.' },
          { num: 4, titulo: 'Clic en "CONFIRMAR ARQUEO"', detalle: 'El arqueo queda guardado como registro de control, con su fecha y la diferencia encontrada — es tu respaldo de que el efectivo físico se revisó y coincide (o no) con el sistema.' },
        ],
      },
      {
        titulo: 'Cerrar la Caja Chica',
        icono: 'Lock',
        pasos: [
          { num: 1, titulo: 'Clic en el ícono de candado', detalle: 'En el panel de detalle de la caja activa.' },
          { num: 2, titulo: 'Confirmar cierre', detalle: 'El saldo restante se registra como egreso de cierre. La caja queda en estado "CERRADA" y no acepta más movimientos.', peligro: 'El cierre es irreversible. Asegúrate de que todos los egresos del período estén registrados antes de cerrar.' },
        ],
      },
    ],
  },

  // ══════════════════════════════════════════════════════════════
  // CONTABILIDAD
  // ══════════════════════════════════════════════════════════════
  {
    id: 'contabilidad',
    requiereModulo: 'contabilidad',
    titulo: 'Contabilidad',
    descripcion: 'Plan de cuentas PCGE, asientos contables, periodos y reportes financieros.',
    icono: 'Calculate',
    colorHex: '#4a148c',
    categoria: 'contabilidad',
    secciones: [
      {
        titulo: 'Configurar el Plan de Cuentas',
        icono: 'AccountTree',
        pasos: [
          {
            num: 1, titulo: 'Ir a Contabilidad → Plan de Cuentas',
            detalle: 'El sistema ya trae el Plan Contable General Empresarial (PCGE) completo precargado — en el ejemplo, 498 cuentas listas para usar, entre 6 y 10 dígitos.',
            imagen: '/manual/contabilidad/01-plan-cuentas.png',
          },
          {
            num: 2, titulo: 'Entender el "Nivel" de cada cuenta',
            detalle: ['Elemento: el primer dígito (1 = Activo, 2 = Inventarios, etc.).', 'Cuenta: 2 dígitos (ej. "10 Efectivo y equivalentes").', 'Subcuenta: 3 dígitos (ej. "101 Caja").', 'Divisionaria: 4 dígitos.', 'Analítica: 6 dígitos o más — estas son las que realmente se usan al registrar un asiento.'],
            tip: 'La columna "Hoja" marcada con "Sí" te dice cuáles cuentas son analíticas (las únicas que aceptan movimientos directos).',
          },
          {
            num: 3, titulo: 'Naturaleza y Saldo normal',
            detalle: 'Cada cuenta ya viene marcada como Deudora o Acreedora, y con su "Saldo normal" (Deudor/Acreedor) — esto es lo que el sistema usa para saber si un aumento va al Debe o al Haber.',
          },
          {
            num: 4, titulo: 'Crear una cuenta personalizada (opcional)',
            detalle: 'Si tu empresa necesita una subcuenta específica que no está en el PCGE estándar, usa "+ NUEVA CUENTA" para agregarla bajo la cuenta padre correspondiente.',
          },
        ],
      },
      {
        titulo: 'Registrar un Asiento Contable',
        icono: 'Edit',
        pasos: [
          { num: 1, titulo: 'Ir a Contabilidad → Asientos Contables', detalle: 'Elige primero la Empresa y el Período (mes-año) en la parte superior — el asiento se registra siempre dentro de un período abierto.' },
          {
            num: 2, titulo: 'Clic en "+ Nuevo Asiento"',
            detalle: 'Completa el Tipo de Documento, la Fecha, la Moneda y la Glosa (una descripción general de qué es el asiento, ej. "Compra de útiles de oficina").',
            imagen: '/manual/contabilidad/02-nuevo-asiento.png',
            tip: 'La sección "Datos del Comprobante / PLE" es opcional, pero completarla ahora te ahorra trabajo cuando generes el Libro Diario para SUNAT.',
          },
          {
            num: 3, titulo: 'Agregar las líneas del asiento',
            detalle: ['Busca la Cuenta Contable por código o nombre (solo cuentas "Hoja"/analíticas).', 'Escribe una Glosa de línea si quieres detallar esa cuenta en particular.', 'Ingresa el monto en Debe o en Haber — nunca ambos en la misma línea.', 'Usa "+ AGREGAR LÍNEA" para sumar cuantas líneas necesites.'],
          },
          {
            num: 4, titulo: 'La partida doble se valida sola',
            detalle: 'Abajo de la tabla, "TOTALES" muestra la suma de Debe y de Haber en tiempo real.',
            alerta: 'El botón "GUARDAR ASIENTO" permanece desactivado mientras el Debe y el Haber no sean exactamente iguales — es la validación automática de partida doble.',
          },
          { num: 5, titulo: 'Guardar (Ctrl+Enter)', detalle: 'El asiento queda registrado en el período seleccionado y disponible para los reportes y el Libro Diario/Mayor.' },
        ],
      },
      {
        titulo: 'Gestionar Períodos Contables',
        icono: 'DateRange',
        pasos: [
          { num: 1, titulo: 'Ir a Períodos', detalle: 'Menú lateral → "Contabilidad" → "Períodos".' },
          { num: 2, titulo: 'Abrir período', detalle: 'Crea y abre el período (mes-año) para poder registrar asientos en él.' },
          { num: 3, titulo: 'Cerrar período', detalle: 'Una vez revisado el mes, ciérralo para impedir modificaciones accidentales.', peligro: 'El cierre de período es irreversible. Verifica que todos los asientos del mes estén correctos antes de cerrar.' },
        ],
      },
      {
        titulo: 'Otros registros y herramientas contables',
        icono: 'MenuBook',
        pasos: [
          {
            num: 1, titulo: 'Registro de Ventas y Registro de Compras',
            detalle: 'Muestran, por Empresa y Período, el detalle de cada comprobante con su Base Gravada, IGV y Total — la base que alimenta al Registro de Compras (6.1) y Registro de Ventas (8.1) del PLE. El botón "+ NUEVO" permite agregar un documento manual si hiciera falta corregir algo.',
            imagenes: ['/manual/contabilidad/03-registro-ventas.png', '/manual/contabilidad/04-registro-compras.png'],
            tip: 'Normalmente no necesitas tocar nada aquí a mano: se llenan solos con las Facturas/Boletas/NC/ND que ya emitiste y las Compras que registraste en Logística.',
          },
          {
            num: 2, titulo: 'Cierre Contable Anual',
            detalle: 'Un asistente de 4 pasos (Selección → Vista previa → Configuración → Ejecutar cierre) que genera los asientos de cierre de las cuentas de resultado (ingresos y gastos) y calcula la utilidad o pérdida del ejercicio. Opcionalmente genera también el asiento de apertura del año siguiente.',
            imagen: '/manual/contabilidad/05-cierre-anual.png',
            alerta: 'Antes de poder analizar el ejercicio, el sistema exige: que todos los períodos del año estén en estado CERRADO, que todos los asientos del año estén cuadrados (Debe = Haber), y que no exista ya un cierre para ese mismo año.',
          },
          {
            num: 3, titulo: 'Conciliación Bancaria',
            detalle: 'Compara el saldo de tu cuenta contable contra el saldo real del extracto bancario. Elige la Empresa y haz clic en "+ NUEVA CONCILIACIÓN" para registrar el Saldo Inicial y el Saldo Extracto de un período, y luego ir marcando qué movimientos ya aparecieron en el banco.',
            imagen: '/manual/contabilidad/06-conciliacion.png',
          },
          {
            num: 4, titulo: 'El resto de reportes (Activos Fijos, Presupuesto, Retenciones y Detracciones, PDT 0621, Centros de Costo, etc.)',
            detalle: 'Todos siguen el mismo patrón: eliges Empresa (y Período cuando aplica) arriba, y el sistema te muestra la tabla o el asistente correspondiente. Son herramientas de análisis y declaración que se apoyan en los asientos ya registrados — no necesitas volver a digitar nada si ya llevas los asientos al día.',
          },
        ],
      },
    ],
  },

  // ══════════════════════════════════════════════════════════════
  // PLE — LIBROS ELECTRÓNICOS
  // ══════════════════════════════════════════════════════════════
  {
    id: 'ple',
    requiereModulo: 'ple',
    titulo: 'Libros Electrónicos PLE',
    descripcion: 'Generación de libros electrónicos para declaración a SUNAT (PLE 5.4).',
    icono: 'MenuBook',
    colorHex: '#004d40',
    categoria: 'contabilidad',
    secciones: [
      {
        titulo: 'Generar un libro electrónico',
        icono: 'FileDownload',
        pasos: [
          {
            num: 1, titulo: 'Ir a Contabilidad → Libros PLE SUNAT',
            detalle: 'Elige la Empresa Contable, el Año y el Mes, y haz clic en "CALCULAR REGISTROS". El sistema arma los 6 libros de una vez: 3.1 Libro Diario, 3.2 Libro Mayor, 5.1 Libro de Caja y Bancos, 6.1 Registro de Compras, 8.1 Registro de Ventas, y 3.3 Libro de Inventarios.',
            imagen: '/manual/ple/01-inicio.png',
            tip: 'Cada tarjeta ya te muestra un resumen (número de registros, totales) antes de descargar nada — así detectas de un vistazo si un libro quedó en 0 registros por error.',
          },
          {
            num: 2, titulo: 'Descargar el Excel primero',
            detalle: 'Cada libro tiene su propio botón "EXCEL" — ábrelo y revisa que los montos cuadren con lo que esperas del mes antes de generar el archivo oficial.',
            tip: 'El Excel es solo para revisión tuya; nunca se sube a SUNAT.',
          },
          {
            num: 3, titulo: 'Descargar el "PLE .TXT"',
            detalle: 'Ese es el archivo en el formato exacto que exige SUNAT para cada libro — con el nombre y estructura de columnas ya armados según la versión PLE vigente.',
          },
          {
            num: 4, titulo: 'Cargar en el aplicativo PLE de SUNAT',
            detalle: 'Ingresa al aplicativo PLE (fuera de InsightSoft, en el portal de SUNAT), selecciona el libro correspondiente y sube el archivo TXT descargado.',
            nota: 'InsightSoft genera el archivo, pero la carga final a SUNAT se hace siempre desde su propio aplicativo — no hay un envío automático como con los comprobantes electrónicos.',
          },
        ],
      },
    ],
  },

  // ══════════════════════════════════════════════════════════════
  // RECURSOS HUMANOS
  // ══════════════════════════════════════════════════════════════
  {
    id: 'rrhh',
    requiereModulo: 'rrhh',
    titulo: 'Recursos Humanos',
    descripcion: 'Gestión de empleados, contratos, vacaciones y planilla de remuneraciones.',
    icono: 'Groups',
    colorHex: '#1a237e',
    categoria: 'rrhh',
    secciones: [
      {
        titulo: 'Registrar un trabajador',
        icono: 'PersonAdd',
        pasos: [
          {
            num: 1, titulo: 'Ir a Recursos Humanos → Trabajadores',
            detalle: 'Filtra por Empresa (si manejas varias razones sociales) y por Estado (Activo/Inactivo). Cada fila muestra el documento, nombre y las acciones disponibles.',
            imagen: '/manual/rrhh/01-lista.png',
          },
          {
            num: 2, titulo: 'Clic en "+ NUEVO TRABAJADOR"',
            detalle: 'El formulario está organizado en 4 pestañas, para no saturar una sola pantalla: PERSONAL, LABORAL, PREVISIONAL y BANCARIO.',
            imagen: '/manual/rrhh/02-nuevo-trabajador.png',
          },
          {
            num: 3, titulo: 'Pestaña PERSONAL',
            detalle: ['Tipo y Número de Documento (DNI/CE) — el ícono de lupa puede consultar el nombre automáticamente.', 'Sexo, Apellido Paterno, Apellido Materno, Nombres.', 'Fecha de Nacimiento, Estado Civil, Teléfono, Email, Dirección.'],
          },
          {
            num: 4, titulo: 'Pestaña LABORAL',
            detalle: ['Fecha de ingreso', 'Cargo / Puesto y área', 'Tipo de contrato (plazo fijo, indeterminado, etc.)', 'Remuneración básica mensual'],
          },
          {
            num: 5, titulo: 'Pestaña PREVISIONAL',
            detalle: 'Aquí se registra si el trabajador está afiliado a la ONP o a una AFP (y a cuál), datos que el sistema usa después para calcular los descuentos correctos en la planilla.',
          },
          {
            num: 6, titulo: 'Pestaña BANCARIO',
            detalle: 'Banco y número de cuenta para el pago de haberes (o cuenta de detracción si aplica).',
            tip: 'Guarda también copia del DNI y del contrato del trabajador en el apartado de adjuntos, si el sistema lo permite en tu versión.',
          },
          { num: 7, titulo: 'Clic en "REGISTRAR"', detalle: 'El trabajador queda disponible de inmediato para incluirse en la siguiente planilla.' },
        ],
      },
      {
        titulo: 'Procesar planilla mensual',
        icono: 'Payments',
        pasos: [
          { num: 1, titulo: 'Ir a Planilla', detalle: 'En RRHH, pestaña "Planilla".' },
          { num: 2, titulo: 'Seleccionar período', detalle: 'Elige el mes y año a procesar.' },
          { num: 3, titulo: 'Calcular haberes', detalle: ['El sistema calcula automáticamente: básico, asignación familiar, horas extra', 'Descuentos: AFP/ONP, adelantos, préstamos', 'Aportes del empleador: ESSALUD, SCTR'] },
          { num: 4, titulo: 'Revisar y aprobar', detalle: 'Verifica el resumen de planilla. Si hay observaciones, corrige antes de aprobar.' },
          { num: 5, titulo: 'Exportar a PDF o Excel', detalle: 'Descarga el reporte de planilla para el archivo y las boletas de pago individuales.' },
        ],
      },
    ],
  },

  // ══════════════════════════════════════════════════════════════
  // USUARIOS Y PERMISOS
  // ══════════════════════════════════════════════════════════════
  {
    id: 'usuarios',
    requiereModulo: null,
    titulo: 'Usuarios y Permisos',
    descripcion: 'Crear usuarios, asignar módulos, series y sucursales. Solo para administradores.',
    icono: 'ManageAccounts',
    colorHex: '#37474f',
    categoria: 'admin',
    soloAdmin: true,
    secciones: [
      {
        titulo: 'Crear un nuevo usuario',
        icono: 'PersonAdd',
        pasos: [
          {
            num: 1, titulo: 'Ir a Mantenimiento → Gestión de Usuarios',
            detalle: 'Se ve la lista de todos los usuarios: nombre de acceso, nombres, apellidos, email, perfil y un interruptor de Estado (activo/inactivo).',
            imagen: '/manual/usuarios/01-lista.png',
          },
          {
            num: 2, titulo: 'Clic en "+ NUEVO USUARIO"',
            detalle: 'Completa Usuario (con el que va a iniciar sesión), Nombres, Apellidos, Email, Perfil y Contraseña.',
            imagen: '/manual/usuarios/02-nuevo-form.png',
            alerta: 'El Perfil "administrador" da acceso total al sistema, incluida la Configuración de Empresa y la creación de otros usuarios. Resérvalo solo para quien realmente lo necesite.',
          },
          {
            num: 3, titulo: 'Guardar y luego ir a "Permisos de Usuario"',
            detalle: 'Crear el usuario NO le da acceso a ningún módulo todavía — eso se configura aparte, en Mantenimiento → Permisos de Usuario, seleccionando el usuario recién creado.',
          },
        ],
      },
      {
        titulo: 'Configurar los permisos de un usuario',
        icono: 'ManageAccounts',
        pasos: [
          {
            num: 1, titulo: 'Ir a Mantenimiento → Permisos de Usuario',
            detalle: 'Elige el usuario en el buscador de arriba. Aparecen varias secciones: Módulos habilitados, Horario de acceso, Sucursales habilitadas, Series por sucursal, y — si aplica — Empresas RR.HH. y Empresas Contables.',
            imagen: '/manual/usuarios/03-permisos-modulos.png',
            tip: '"Panel de inicio (KPIs)" viene activado por defecto para todos — es el dashboard, no un módulo operativo.',
          },
          {
            num: 2, titulo: 'Marcar los módulos a los que tendrá acceso',
            detalle: 'Activa solo lo que ese usuario realmente necesita usar (Facturación, Logística, Tesorería, Contabilidad, etc.). Si un módulo no está marcado, ese usuario ni siquiera va a ver esa opción en su menú lateral.',
          },
          {
            num: 3, titulo: 'Horario de acceso (opcional)',
            detalle: 'Por defecto cualquier usuario puede entrar a cualquier hora. Si activas la restricción, puedes limitarlo a un horario y días específicos — fuera de ese rango no podrá iniciar sesión, y si ya estaba conectado, se le cierra la sesión en su siguiente acción.',
            tip: 'Útil para vendedores de un turno fijo, para que no puedan operar el sistema fuera de su horario de trabajo.',
          },
          {
            num: 4, titulo: 'Sucursales habilitadas',
            detalle: 'Marca a qué sucursales puede acceder. Si el usuario no tiene ninguna restricción marcada aquí, el sistema le muestra todas por defecto.',
          },
          {
            num: 5, titulo: 'Series por sucursal',
            detalle: 'Elige la sucursal y marca qué series de comprobantes puede usar ese usuario en ella (F001, B001, C001, N001, etc.).',
            imagen: '/manual/usuarios/04-permisos-series.png',
            tip: 'Un cajero que solo debe emitir boletas de un punto de venta específico debería tener marcada únicamente esa serie de Boleta, no todas.',
          },
          {
            num: 6, titulo: 'Empresas RR.HH. / Empresas Contables',
            detalle: 'Si tu instalación maneja varias razones sociales en Contabilidad o RR.HH., aquí decides a cuáles puede entrar este usuario en particular.',
          },
        ],
      },
      {
        titulo: 'Cambiar contraseña',
        icono: 'Key',
        pasos: [
          { num: 1, titulo: 'El propio usuario', detalle: 'Puede cambiarla desde su perfil (ícono de usuario arriba a la derecha).' },
          { num: 2, titulo: 'El administrador', detalle: 'Puede resetear la contraseña de cualquier usuario desde el módulo Usuarios.' },
        ],
      },
    ],
  },

  // ══════════════════════════════════════════════════════════════
  // CONFIGURACIÓN DE EMPRESA
  // ══════════════════════════════════════════════════════════════
  {
    id: 'configuracion',
    requiereModulo: null,
    titulo: 'Configuración de Empresa',
    descripcion: 'Datos de empresa, logo, series de comprobantes y configuración SUNAT.',
    icono: 'Settings',
    colorHex: '#455a64',
    categoria: 'admin',
    soloAdmin: true,
    secciones: [
      {
        titulo: 'Configurar datos de la empresa',
        icono: 'Business',
        pasos: [
          {
            num: 1, titulo: 'Ir a Mantenimiento → Configuración Empresa',
            detalle: 'Solo el administrador del sistema puede ver y modificar esta pantalla.',
            imagen: '/manual/configuracion/01-datos-generales.png',
          },
          {
            num: 2, titulo: 'Datos Generales',
            detalle: ['RUC y Razón Social (obligatorios) — el ícono de la brújula junto al RUC consulta los datos en SUNAT.', 'Nombre Comercial, Dirección Fiscal y Ubigeo.', 'Teléfono/Celular y Email principal.', 'Nombre en voucher/ticket — el texto que aparece en la impresión térmica.'],
          },
          {
            num: 3, titulo: 'Logo y firma electrónica',
            detalle: '"SUBIR LOGO" cambia la imagen que aparece en el PDF de tus comprobantes. "SUBIR FIRMA ELECTRÓNICA PFX" carga el certificado digital que firma cada XML antes de enviarlo a SUNAT — sin este archivo el sistema no puede emitir ningún comprobante electrónico.',
            peligro: 'El certificado PFX y su clave son tan sensibles como una firma legal. Solo el administrador debe manejarlos.',
          },
          {
            num: 4, titulo: 'Configuración SUNAT',
            detalle: ['Usuario SOL secundario y Clave SOL secundaria — las credenciales del usuario de facturación electrónica (no la clave SOL principal del representante legal).', 'Nombre y Clave de la firma electrónica.', 'Usuario/Clave de guía de remisión y Client ID (API SUNAT), si tu empresa usa esos servicios adicionales.'],
            alerta: 'Nunca compartas estas credenciales por fuera del sistema. Todas se guardan enmascaradas (puntos) y solo se usan internamente para conectarse con SUNAT.',
          },
          {
            num: 5, titulo: 'Modo Producción vs. Beta',
            detalle: 'Más abajo en esta misma pantalla está el botón de "Modo Producción". Mientras esté en beta, los comprobantes se envían al ambiente de pruebas de SUNAT (no tienen validez legal). Solo actívalo cuando la empresa esté lista para facturar de verdad.',
            nota: 'Este botón es el único lugar del sistema donde se activa el envío real a SUNAT — revísalo con cuidado antes de presionarlo.',
          },
        ],
      },
    ],
  },

];

export default MODULOS;
